export enum BasicWordOrder {
  SubjectVerbObject = "SVO",
  SubjectObjectVerb = "SOV",
  VerbSubjectObject = "VSO",
}

export enum Prepositions {
  Prepositions = "prepositions",
  Postpositions = "postpositions",
  Both = "both",
}

export enum Position {
  PreVerb = "pre-verb",
  PostVerb = "post-verb",
}

export interface Grammar {
  name: string;
  description: string;
  wordOrder: BasicWordOrder;
  definiteArticle: boolean;
  indefiniteArticle: boolean;
  nominalGender: boolean;
  countNouns: boolean;
  prepositions: Prepositions;
  adjunctPosition: Position;
  verbConjugation: boolean;
  nominalInflection: boolean;
  postNominalAdjectives: boolean;
}

export interface RenderUnit {
  text: string;
  highlight: boolean
}

function join(...args: (RenderUnit | RenderUnit[] | null | undefined)[]): RenderUnit[] {
  // @ts-ignore
  const r: RenderUnit[] = [];
  for (const arg of args) {
    if (arg == null) {
      continue;
    }

    if (Array.isArray(arg)) {
      for (const subarg of arg) {
        r.push(subarg);
      }
    } else if (arg.text !== "") {
      r.push(arg);
    }
  }
  return r;
}

abstract class Constituent {
  abstract materialize(grammar: Grammar): RenderUnit[];
}

export interface WordOptions {
  highlight?: boolean;
  nounInflection?: string;
  verbConjugation?: string;
  genderSuffix?: string;
  definite?: boolean;
  indefinite?: boolean;
}

export class Word extends Constituent {
  private text: string;
  private highlight: boolean;
  private nounInflection?: string;
  private verbConjugation?: string;
  private genderSuffix?: string;
  private isDefiniteArticle: boolean;
  private isIndefiniteArticle: boolean;

  constructor(text: string, options?: WordOptions) {
    super();
    this.text = text;
    this.highlight = options?.highlight ?? false;
    this.nounInflection = options?.nounInflection;
    this.verbConjugation = options?.verbConjugation;
    this.genderSuffix = options?.genderSuffix;
    this.isDefiniteArticle = options?.definite ?? false;
    this.isIndefiniteArticle = options?.indefinite ?? false;
  }

  static simple(text: string, options?: WordOptions): Word {
    return new Word(text, options);
  }

  materialize(grammar: Grammar): RenderUnit[] {
    if (!grammar.definiteArticle && this.isDefiniteArticle) {
      return [];
    }

    if (!grammar.indefiniteArticle && this.isIndefiniteArticle) {
      return [];
    }

    let r = this.text;

    if (grammar.nominalInflection && this.nounInflection) {
      r += this.nounInflection;
    } else {
      if (grammar.nominalGender && this.genderSuffix) {
        r += this.genderSuffix;
      }
    }

    if (grammar.verbConjugation && this.verbConjugation) {
      r += this.verbConjugation;
    }

    return [{text: r, highlight: this.highlight}];
  }
}

export class NounPhrase extends Constituent {
  private determiner: Constituent | null;
  private modifier: Constituent | null;
  private head: Constituent | null;
  private relativeClause: Constituent | null;
  private classifier: Constituent | null;

  constructor(determiner: Constituent | null, modifier: Constituent | null, head: Constituent, relativeClause: Constituent | null, classifier: Constituent | null) {
    super();
    this.determiner = determiner;
    this.modifier = modifier;
    this.head = head;
    this.relativeClause = relativeClause;
    this.classifier = classifier;
  }


  materialize(grammar: Grammar): RenderUnit[] {
    if (grammar.postNominalAdjectives) {
      return join(
        grammar.countNouns ? this.determiner?.materialize(grammar) : null,
        !grammar.countNouns ? this.classifier?.materialize(grammar) : null,
        this.head?.materialize(grammar),
        this.modifier?.materialize(grammar),
        this.relativeClause?.materialize(grammar)
      );
    } else {
      return join(
        grammar.countNouns ? this.determiner?.materialize(grammar) : null,
        !grammar.countNouns ? this.classifier?.materialize(grammar) : null,
        this.modifier?.materialize(grammar),
        this.head?.materialize(grammar),
        this.relativeClause?.materialize(grammar)
      );
    }
  }
}

export class VerbPhrase extends Constituent {
  private auxiliary: Constituent | null;
  private head: Constituent;

  constructor(auxiliary: Constituent | null, head: Constituent) {
    super();
    this.auxiliary = auxiliary;
    this.head = head;
  }


  materialize(grammar: Grammar): RenderUnit[] {
    return join(this.auxiliary?.materialize(grammar), this.head.materialize(grammar));
  }
}

export class PrepositionalPhrase extends Constituent {
  private head: Constituent;
  private object: Constituent;
  private other: Constituent | null;

  constructor(head: Constituent, object: Constituent, other: Constituent | null) {
    super();
    this.head = head;
    this.object = object;
    this.other = other;
  }

  materialize(grammar: Grammar): RenderUnit[] {
    switch (grammar.prepositions) {
      case Prepositions.Prepositions:
        return join(this.head.materialize(grammar), this.object.materialize(grammar));
      case Prepositions.Postpositions:
        return join(this.object.materialize(grammar), this.head.materialize(grammar));
      case Prepositions.Both:
        return join(this.other?.materialize(grammar), this.object.materialize(grammar), this.head.materialize(grammar));
    }
  }
}

export interface TranslationAttrs {
  units: RenderUnit[];
  spaces: boolean;
  period: string;
}

export interface Translations {
  russian: TranslationAttrs;
  russianTranslit: TranslationAttrs;
  french: TranslationAttrs;
  chinese: TranslationAttrs;
  chineseTranslit: TranslationAttrs;
  hindi: TranslationAttrs;
  hindiTranslit: TranslationAttrs;
}

export class Sentence extends Constituent {
  private subject: Constituent;
  private predicate: Constituent;
  private object: Constituent | null;
  private adjunct: Constituent | null;
  public translations: Translations;

  constructor(subject: Constituent, predicate: Constituent, object: Constituent | null, adjunct: Constituent | null, translations: Translations) {
    super();
    this.subject = subject;
    this.predicate = predicate;
    this.object = object;
    this.adjunct = adjunct;
    this.translations = translations;
  }

  materialize(grammar: Grammar): RenderUnit[] {
    let r;

    const adjunct = this.adjunct?.materialize(grammar);
    const preAdjunct = grammar.adjunctPosition === Position.PreVerb ? adjunct : null;
    const postAdjunct = grammar.adjunctPosition === Position.PostVerb ? adjunct : null;

    switch (grammar.wordOrder) {
      case BasicWordOrder.SubjectObjectVerb:
        r = join(
          this.subject.materialize(grammar),
          preAdjunct,
          this.object?.materialize(grammar),
          this.predicate.materialize(grammar),
          postAdjunct,
        );
        break;
      case BasicWordOrder.SubjectVerbObject:
        r = join(
          this.subject.materialize(grammar),
          preAdjunct,
          this.predicate.materialize(grammar),
          this.object?.materialize(grammar),
          postAdjunct,
        );
        break;
      case BasicWordOrder.VerbSubjectObject:
        r = join(
          preAdjunct,
          this.predicate.materialize(grammar),
          this.subject.materialize(grammar),
          this.object?.materialize(grammar),
          postAdjunct,
        );
        break;
    }

    return r;
  }
}
